import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VaultService } from "@/services/vault";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onUnlocked?: (expiresAt: string) => void;
};

export function VaultUnlockModal({ open, onOpenChange, onUnlocked }: Props) {
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [needsSetup, setNeedsSetup] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Password managers and OS OTP suggestions need a focused, named OTP field inside a form.
    useEffect(() => {
        if (!open) return;
        setCode("");
        setNeedsSetup(false);
        const id = window.setTimeout(() => inputRef.current?.focus(), 50);
        return () => window.clearTimeout(id);
    }, [open]);

    const handleUnlock = async (event?: FormEvent) => {
        event?.preventDefault();
        if (!code.trim()) {
            toast.error("Enter your TOTP code");
            return;
        }
        setSubmitting(true);
        setNeedsSetup(false);
        try {
            const status = await VaultService.totpStatus();
            if (status.needs_setup) {
                setNeedsSetup(true);
                return;
            }
            const res = await VaultService.unlock(code.trim());
            toast.success("Vault unlocked for 30 minutes");
            setCode("");
            onOpenChange(false);
            onUnlocked?.(res.expires_at);
        } catch (e: any) {
            const detail = e?.json?.detail || e?.message || "Unlock failed";
            toast.error(typeof detail === "string" ? detail : "Unlock failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Unlock vault</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUnlock} autoComplete="on" className="space-y-4 py-2">
                    <p className="text-sm text-muted-foreground">
                        Enter the 6-digit code from your authenticator app to reveal passwords for 30 minutes.
                    </p>
                    {needsSetup && (
                        <p className="text-sm text-orange-700">
                            TOTP is not configured yet.{" "}
                            <Link to="/parametres/coffre" className="underline" onClick={() => onOpenChange(false)}>
                                Set it up in Settings
                            </Link>
                            .
                        </p>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="one-time-code">Authentication code</Label>
                        <Input
                            ref={inputRef}
                            id="one-time-code"
                            name="one-time-code"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            autoComplete="one-time-code"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            maxLength={6}
                            value={code}
                            onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="123456"
                            enterKeyHint="done"
                        />
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={submitting || code.length < 6}>
                            Unlock
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
